<template>
    <vx-expandable-card>
        <template v-slot:header>Connection</template>
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div>
                    <div class="font-bold mb-1">Details for your identity provider</div>
                    <div class="text-sm text-slate-600 mb-3">
                        Create a SAML application in your identity provider (Microsoft Entra ID, Okta, Google Workspace,
                        AD FS) with these values.
                    </div>
                    <div class="grid gap-2 text-sm sm:grid-cols-[auto_1fr_auto] sm:items-center">
                        <template v-for="row in spRows" :key="row.key">
                            <span class="text-slate-500">{{ row.label }}</span>
                            <code class="break-all" :data-test="`sp-${row.key}`">{{ row.value }}</code>
                            <vx-button
                                size="xs"
                                color="muted"
                                @click="copyText(row.value)"
                                :data-test="`copy-sp-${row.key}`"
                            >
                                Copy <font-awesome-icon widthAuto :icon="faCopy" />
                            </vx-button>
                        </template>
                    </div>
                </div>

                <page-loading v-if="loading" />

                <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="load-failed">
                    <div class="flex items-center justify-between">
                        The connection could not be loaded.
                        <vx-button size="sm" color="danger" @click="loadConnection" data-test="retry-load">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <template v-else>
                    <div
                        v-if="connection"
                        class="flex items-center justify-between rounded-lg border border-slate-200 p-4"
                        data-test="connection-status"
                    >
                        <div>
                            <div class="font-bold">{{ form.enabled ? 'Enabled' : 'Disabled' }}</div>
                            <div class="text-sm text-slate-500">
                                <template v-if="connection.last_login_at"
                                    >Last SSO login {{ fromNow(connection.last_login_at) }}</template
                                >
                                <template v-else>No SSO login yet</template>
                            </div>
                        </div>
                        <vx-toggle v-model="form.enabled" size="md" data-test="enabled-toggle" />
                    </div>

                    <div>
                        <vx-label>Identity provider</vx-label>
                        <vx-selectable
                            :modelValue="preset ?? undefined"
                            @update:modelValue="applyPreset"
                            :options="IDP_PRESETS"
                            label="label"
                            :reduce="(option: IdpPreset) => option.key"
                            placeholder="Choose a preset to fill the attribute names"
                            data-test="preset"
                        />
                    </div>

                    <vx-disclosure data-test="metadata-import">
                        <template v-slot:header>Paste identity provider metadata XML</template>
                        <template v-slot:content>
                            <vx-input
                                v-model="metadataXml"
                                :rows="6"
                                size="lg"
                                placeholder="<EntityDescriptor …>"
                                data-test="metadata-xml"
                                class="text-sm"
                            />
                            <div class="flex items-center gap-3 mt-2">
                                <vx-button
                                    size="sm"
                                    color="muted-dark"
                                    @click="importMetadata"
                                    data-test="import-metadata"
                                    >Fill from metadata</vx-button
                                >
                                <span v-if="metadataError" class="text-xs text-rose-800" data-test="metadata-error">{{
                                    metadataError
                                }}</span>
                            </div>
                        </template>
                    </vx-disclosure>

                    <div>
                        <vx-label>Identity provider entity ID</vx-label>
                        <vx-input
                            v-model="form.idp_entity_id"
                            size="lg"
                            data-test="idp-entity-id"
                            :error="displayFirstError(validation.idp_entity_id.$errors)"
                        />
                    </div>

                    <div>
                        <vx-label>
                            Identity provider login URL
                            <template v-slot:description
                                >The HTTP-Redirect single sign-on URL; must start with https://</template
                            >
                        </vx-label>
                        <vx-input
                            v-model="form.idp_sso_url"
                            size="lg"
                            type="url"
                            data-test="idp-sso-url"
                            :error="displayFirstError(validation.idp_sso_url.$errors)"
                        />
                    </div>

                    <div>
                        <vx-label>
                            NameID format
                            <template v-slot:description>
                                Persistent: a stable identifier, so an email change at the identity provider follows the
                                same Voxie account. Email address or Unspecified: a changed email is a new identity.
                            </template>
                        </vx-label>
                        <vx-radio-selection v-model="form.name_id_format" data-test="name-id-format">
                            <vx-radio-selection-item
                                v-for="option in NAME_ID_FORMAT_OPTIONS"
                                :key="option.value"
                                :value="option.value"
                                :data-test="`name-id-format-${option.value}`"
                            >
                                <template v-slot:header>{{ option.label }}</template>
                            </vx-radio-selection-item>
                        </vx-radio-selection>
                    </div>

                    <div class="flex flex-col gap-3">
                        <vx-label>
                            Signing certificates
                            <template v-slot:description>
                                One to five X.509 certificates in PEM format. To rotate, add the new certificate next to
                                the current one before activating it at the identity provider, then remove the old one.
                            </template>
                        </vx-label>
                        <div
                            v-for="(certificate, index) in form.certificates"
                            :key="certificate.key"
                            class="flex gap-2"
                            data-test="certificate"
                        >
                            <div class="grow">
                                <vx-input
                                    v-model="certificate.value"
                                    :rows="4"
                                    size="lg"
                                    class="font-mono text-xs"
                                    :data-test="`certificate-${index}`"
                                    :error="displayFirstError(certificateErrors(index))"
                                />
                                <div
                                    v-if="savedCertificate(certificate.value)"
                                    class="text-xs mt-1"
                                    :class="certificateClass(savedCertificate(certificate.value)!)"
                                    :data-test="`certificate-expiry-${index}`"
                                >
                                    {{ certificateSummary(savedCertificate(certificate.value)!) }}
                                </div>
                            </div>
                            <vx-button
                                v-if="form.certificates.length > 1"
                                color="muted-light"
                                class="w-12"
                                @click="removeCertificate(index)"
                                :data-test="`remove-certificate-${index}`"
                            >
                                <font-awesome-icon :icon="faTrash" />
                            </vx-button>
                        </div>
                        <vx-button
                            v-if="form.certificates.length < MAX_CERTIFICATES"
                            size="sm"
                            color="muted-dark"
                            class="self-start"
                            @click="addCertificate"
                            data-test="add-certificate"
                        >
                            Add certificate <font-awesome-icon widthAuto :icon="faCirclePlus" />
                        </vx-button>
                    </div>

                    <div>
                        <vx-label>Email address comes from</vx-label>
                        <vx-radio-selection v-model="form.email_source" data-test="email-source">
                            <vx-radio-selection-item value="name_id" data-test="email-source-name-id">
                                <template v-slot:header>The NameID</template>
                                <template v-slot:content
                                    >The identity provider sends the email address as the subject NameID (Google
                                    Workspace default).</template
                                >
                            </vx-radio-selection-item>
                            <vx-radio-selection-item value="attribute" data-test="email-source-attribute">
                                <template v-slot:header>An attribute</template>
                                <template v-slot:content
                                    >The identity provider sends the email address in a named attribute (Entra ID, Okta
                                    and AD FS default).</template
                                >
                            </vx-radio-selection-item>
                        </vx-radio-selection>
                        <vx-input
                            v-if="form.email_source === 'attribute'"
                            v-model="form.email_attribute"
                            size="lg"
                            class="mt-2"
                            placeholder="email"
                            data-test="email-attribute"
                            :error="displayFirstError(validation.email_attribute.$errors)"
                        />
                    </div>

                    <div class="flex flex-col gap-2">
                        <vx-label>
                            Name attributes
                            <template v-slot:description
                                >Up to three attributes joined with spaces to name a new user. Names are set once, when
                                the account is created.</template
                            >
                        </vx-label>
                        <div
                            v-for="(attribute, index) in form.name_attributes"
                            :key="attribute.key"
                            class="flex gap-2"
                            data-test="name-attribute"
                        >
                            <vx-input
                                v-model="attribute.value"
                                size="lg"
                                class="grow"
                                :data-test="`name-attribute-${index}`"
                            />
                            <vx-button
                                color="muted-light"
                                class="w-12"
                                @click="removeNameAttribute(index)"
                                :data-test="`remove-name-attribute-${index}`"
                            >
                                <font-awesome-icon :icon="faTrash" />
                            </vx-button>
                        </div>
                        <vx-button
                            v-if="form.name_attributes.length < MAX_NAME_ATTRIBUTES"
                            size="sm"
                            color="muted-dark"
                            class="self-start"
                            @click="addNameAttribute"
                            data-test="add-name-attribute"
                        >
                            Add attribute <font-awesome-icon widthAuto :icon="faCirclePlus" />
                        </vx-button>
                    </div>

                    <div class="flex items-center justify-between">
                        <div>
                            <vx-label>Allow login started from the identity provider</vx-label>
                            <div class="text-sm text-slate-500">
                                Lets members open Voxie from their identity provider's app portal. Off by default.
                            </div>
                        </div>
                        <vx-toggle v-model="form.allow_idp_initiated" size="md" data-test="allow-idp-initiated" />
                    </div>

                    <div>
                        <vx-label>
                            Session lifetime
                            <template v-slot:description
                                >Between 15 minutes and 8 hours (480). Members log in again with SSO after that, or
                                sooner if the identity provider limits the session.</template
                            >
                        </vx-label>
                        <vx-input
                            v-model="form.session_lifetime_minutes"
                            type="number"
                            size="lg"
                            :min="SESSION_LIFETIME_MINUTES_MIN"
                            :max="SESSION_LIFETIME_MINUTES_MAX"
                            class="max-w-[200px]"
                            data-test="session-lifetime"
                            :error="displayFirstError(validation.session_lifetime_minutes.$errors)"
                        >
                            <template v-slot:right>minutes</template>
                        </vx-input>
                    </div>

                    <vx-alert v-if="conflict" color="danger" :closeable="false" data-test="conflict">{{
                        conflict
                    }}</vx-alert>

                    <div class="flex flex-col gap-2 sm:flex-row sm:justify-between">
                        <vx-button
                            size="lg"
                            color="primary"
                            :loading="saving"
                            @click="save"
                            data-test="save-connection"
                        >
                            {{ connection ? 'Save changes' : 'Connect' }}
                        </vx-button>
                        <vx-button
                            v-if="connection"
                            size="lg"
                            color="danger"
                            outline
                            @click="removeConnection"
                            data-test="remove-connection"
                        >
                            Remove connection <font-awesome-icon widthAuto :icon="faTrash" />
                        </vx-button>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faCirclePlus, faCopy, faRefresh, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxDisclosure,
    VxExpandableCard,
    VxInput,
    VxLabel,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
    VxSwal,
    VxSwalError,
    VxToggle,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { between, helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCopyText } from '~/composables/useCopyText';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type {
    SsoCertificate,
    SsoConfig,
    SsoConnection,
    SsoEmailSource,
    SsoNameIdFormat,
} from '~/services/ssoClient.types';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { fromNow, shortDate } from '~/utils/date';
import incrementingId from '~/utils/incrementingId';
import { throwSwalRequestError } from '~/utils/request';
import { displayFirstError, validateRules } from '~/utils/validation';
import {
    IDP_PRESETS,
    MAX_CERTIFICATES,
    MAX_NAME_ATTRIBUTES,
    NAME_ID_FORMAT_OPTIONS,
    SESSION_LIFETIME_MINUTES_DEFAULT,
    SESSION_LIFETIME_MINUTES_MAX,
    SESSION_LIFETIME_MINUTES_MIN,
    certificateState,
    certificateStateClass,
    conflictText,
    parseIdpMetadata,
    type IdpPreset,
} from './ssoSettingsHelpers';

interface KeyedValue {
    key: number;
    value: string;
}

const props = defineProps<{
    teamId: number;
    sp: SsoConfig['sp'];
}>();

const emit = defineEmits<{
    changed: [];
}>();

const toasted = useToasted();
const { copyText } = useCopyText();
const { $externalResults, catchError } = useFormRequestHandler();

const connection = ref<SsoConnection | null>(null);
const loading = ref(true);
const loadFailed = ref(false);
const saving = ref(false);
const preset = ref<IdpPreset['key'] | null>(null);
const metadataXml = ref('');
const metadataError = ref('');
const conflict = ref<string | null>(null);

const keyed = (value = ''): KeyedValue => ({ key: incrementingId(), value });

const defaultForm = () => ({
    enabled: true,
    idp_entity_id: '',
    idp_sso_url: '',
    name_id_format: 'unspecified' as SsoNameIdFormat,
    certificates: [keyed()],
    email_source: 'attribute' as SsoEmailSource,
    email_attribute: '',
    name_attributes: [] as KeyedValue[],
    allow_idp_initiated: false,
    session_lifetime_minutes: SESSION_LIFETIME_MINUTES_DEFAULT,
});

const form = reactive(defaultForm());

const validation = useVuelidate(
    {
        idp_entity_id: {
            required,
            maxLength: maxLength(1024),
            noWhitespace: helpers.withMessage('The entity ID cannot contain spaces.', helpers.regex(/^\S+$/)),
        },
        idp_sso_url: {
            required,
            https: helpers.withMessage('The login URL must start with https://', helpers.regex(/^https:\/\/\S+$/)),
        },
        email_attribute: { required: requiredIf(() => form.email_source === 'attribute'), maxLength: maxLength(191) },
        session_lifetime_minutes: {
            required,
            between: between(SESSION_LIFETIME_MINUTES_MIN, SESSION_LIFETIME_MINUTES_MAX),
        },
    },
    form,
    { $externalResults }
);

const spRows = computed(() => [
    { key: 'entity-id', label: 'Entity ID', value: props.sp.entity_id },
    { key: 'metadata-url', label: 'Metadata URL', value: props.sp.metadata_url },
    { key: 'acs-url', label: 'ACS URL', value: props.sp.acs_url },
    { key: 'login-url', label: 'Login URL', value: props.sp.login_url },
]);

const fillForm = (source: SsoConnection) => {
    form.enabled = source.enabled;
    form.idp_entity_id = source.idp_entity_id;
    form.idp_sso_url = source.idp_sso_url;
    form.name_id_format = source.name_id_format;
    form.certificates = source.certificates.map((certificate) => keyed(certificate.certificate));
    form.email_source = source.email_source;
    form.email_attribute = source.email_attribute ?? '';
    form.name_attributes = source.name_attributes.map((attribute) => keyed(attribute));
    form.allow_idp_initiated = source.allow_idp_initiated;
    form.session_lifetime_minutes = source.session_lifetime_minutes;
};

const loadConnection = async () => {
    loading.value = true;
    loadFailed.value = false;

    try {
        const { data } = await ssoClient.getConnection(props.teamId);
        connection.value = data;
        fillForm(data);
    } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status !== HTTP_NOT_FOUND) {
            loadFailed.value = true;
        }
    } finally {
        loading.value = false;
    }
};

const applyPreset = (key: IdpPreset['key'] | null) => {
    preset.value = key;
    const chosen = IDP_PRESETS.find((candidate) => candidate.key === key);

    if (chosen === undefined) {
        return;
    }

    form.name_id_format = chosen.name_id_format;
    form.email_source = chosen.email_source;
    form.email_attribute = chosen.email_attribute ?? '';
    form.name_attributes = chosen.name_attributes.map((attribute) => keyed(attribute));
};

const importMetadata = () => {
    const parsed = parseIdpMetadata(metadataXml.value);

    if (parsed === null) {
        metadataError.value =
            'Paste the metadata of one SAML identity provider that offers an HTTP-Redirect login URL.';
        return;
    }

    metadataError.value = '';
    form.idp_entity_id = parsed.idp_entity_id;
    form.idp_sso_url = parsed.idp_sso_url;
    form.certificates = parsed.certificates.length
        ? parsed.certificates.map((certificate) => keyed(certificate))
        : [keyed()];
};

const savedCertificate = (pem: string): SsoCertificate | undefined =>
    connection.value?.certificates.find((certificate) => certificate.certificate === pem);

const certificateSummary = (certificate: SsoCertificate): string => {
    const state = certificateState(certificate);
    const expiry = shortDate(certificate.not_after);

    if (state === 'expired') {
        return `Expired on ${expiry}`;
    }

    return `${state === 'expiring' ? 'Expires soon: ' : 'Valid until '}${expiry} · ${certificate.subject}`;
};

const certificateClass = (certificate: SsoCertificate): string => certificateStateClass[certificateState(certificate)];

const certificateErrors = (index: number) => $externalResults.value[`certificates.${index}`] ?? [];

const addCertificate = () => form.certificates.push(keyed());
const removeCertificate = (index: number) => form.certificates.splice(index, 1);
const addNameAttribute = () => form.name_attributes.push(keyed());
const removeNameAttribute = (index: number) => form.name_attributes.splice(index, 1);

const payload = () => ({
    enabled: form.enabled,
    idp_entity_id: form.idp_entity_id.trim(),
    idp_sso_url: form.idp_sso_url.trim(),
    name_id_format: form.name_id_format,
    certificates: form.certificates.map((certificate) => certificate.value.trim()),
    email_source: form.email_source,
    email_attribute: form.email_source === 'attribute' ? form.email_attribute.trim() : null,
    name_attributes: form.name_attributes.map((attribute) => attribute.value.trim()).filter((value) => value !== ''),
    allow_idp_initiated: form.allow_idp_initiated,
    session_lifetime_minutes: Number(form.session_lifetime_minutes),
});

const save = async () => {
    if (saving.value || !(await validateRules(validation))) {
        return;
    }

    form.certificates = form.certificates.filter((certificate) => certificate.value.trim() !== '');

    if (form.certificates.length === 0) {
        form.certificates = [keyed()];
        $externalResults.value = { 'certificates.0': ['Add at least one signing certificate.'] };
        return;
    }

    saving.value = true;
    conflict.value = null;

    try {
        const { data } = await ssoClient.updateConnection(props.teamId, payload());
        const created = connection.value === null;
        connection.value = data;
        fillForm(data);
        toasted.success(created ? 'Identity provider connected.' : 'Connection saved.');
        emit('changed');
    } catch (error) {
        conflict.value = conflictText(error);

        if (conflict.value === null) {
            catchError(error);
        }
    } finally {
        saving.value = false;
    }
};

const removeConnection = () => {
    VxSwal.confirmation(
        'Remove connection',
        'Members will no longer be able to log in with SSO and their SSO identities will be unlinked. If you connect an identity provider again, each member is claimed again at their next SSO login (password reset, tokens and devices revoked). There is no undo.',
        'danger',
        () =>
            ssoClient
                .deleteConnection(props.teamId)
                .then(() => {
                    connection.value = null;
                    Object.assign(form, defaultForm());
                    preset.value = null;
                    emit('changed');
                })
                .catch((error) => {
                    const text = conflictText(error);

                    if (text !== null) {
                        throw new VxSwalError(text);
                    }

                    throwSwalRequestError(error);
                })
    );
};

onMounted(loadConnection);
</script>
